import { useMedia } from "@next-platform/client-common-spicy-ui";
import { useLanguage } from "@next-platform/client-common-core";
import { Accordion, AccordionItem, Image, Spacer } from "@nextui-org/react";
import { useTheme } from "next-themes";
import { useState } from "react";

import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import TextStyles from "@/components/common/TextStyles";
import { Col, Row, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { getMonthLabel } from "@/utils";

import LeaderboardListing from "./components/LeaderboardListing";
import PrizeCard from "./components/PrizeCard";

const TROPHY_LOGO = "/Assets/trophy.svg";
const SPACER_Y_MOBILE = 6;
const SPACER_Y_DESKTOP = 10;

const boldTransComponents = { b: <b /> };
const prizeTransComponents = { b: <b className="text-black dark:text-white" /> };
const accordionClassNames = {
  heading: TextStyles.accordion.heading + " w-min whitespace-nowrap",
  trigger: TextStyles.accordion.trigger + " pb-2",
  title: TextStyles.accordion.title,
  indicator: TextStyles.accordion.indicator + " relative bottom-1",
};

export default function CreatorsLeaderboard() {
  const [stateSelectedDate, setStateSelectedDate] = useState<Set<string>>(new Set());
  const { currentLanguage } = useLanguage();
  const { isMobile } = useMedia();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const month = [...stateSelectedDate][0];

  return (
    <Col>
      <PageStaticMetaTags pageId="creatorLeaderboard" />
      <Spacer y={4} />
      <Row gap={0.5}>
        <Image src={TROPHY_LOGO} className="relative top-1.5" alt="" />
        <Text h1 b variant="xxl">
          <Trans i18nKey="leaderboard:page.title" />
        </Text>
      </Row>
      <Col className={isMobile ? undefined : "pl-7"}>
        <Text>{getMonthLabel(month, currentLanguage)}</Text>
        <Spacer y={4} />
        <Text variant="regular">
          <Trans i18nKey="leaderboard:page.welcome" />
        </Text>
        <Spacer y={1} />
        <Accordion className="px-0">
          <AccordionItem
            title={
              <Text h4>
                <Trans i18nKey="leaderboard:page.howItWorks.title" />
              </Text>
            }
            classNames={accordionClassNames}
          >
            <Text variant="regular">
              <Trans i18nKey="leaderboard:page.howItWorks.description" />
            </Text>
            <Spacer y={2} />
            <ul className="*:list-disc pl-5">
              <li>
                <Text variant="regular">
                  <Trans i18nKey="leaderboard:page.howItWorks.topNewCreators" />
                </Text>
              </li>
              <li>
                <Text variant="regular">
                  <Trans i18nKey="leaderboard:page.howItWorks.topAllCreators" />
                </Text>
              </li>
            </ul>
            <Spacer y={2} />
            <Text type={!isDark ? "text-yellow-600" : "under_review"}>
              <Trans i18nKey="leaderboard:page.howItWorks.rankingNote" />
            </Text>
            <Spacer y={isMobile ? SPACER_Y_MOBILE : SPACER_Y_DESKTOP} />
            <Text b variant="lg">
              <Trans i18nKey="leaderboard:page.prizes.title" />
            </Text>
            <Spacer y={5} />
            <Row gap={1.5} className={isMobile ? "flex-col" : undefined}>
              <PrizeCard
                title={
                  <Text>
                    <Trans i18nKey="leaderboard:page.prizes.top3.title" components={boldTransComponents} />
                  </Text>
                }
                description={
                  <Text type={!isDark ? "text-yellow-600" : "under_review"}>
                    <Trans i18nKey="leaderboard:page.prizes.top3.description" components={prizeTransComponents} />
                  </Text>
                }
              />
              <PrizeCard
                title={
                  <Text>
                    <Trans i18nKey="leaderboard:page.prizes.top7.title" components={boldTransComponents} />
                  </Text>
                }
                description={
                  <Text type={!isDark ? "text-yellow-600" : "under_review"}>
                    <Trans i18nKey="leaderboard:page.prizes.top7.description" components={prizeTransComponents} />
                  </Text>
                }
              />
            </Row>

            <Spacer y={2} />
            <Text>
              <Trans i18nKey="leaderboard:page.prizes.note" />
            </Text>
            <Spacer y={isMobile ? SPACER_Y_MOBILE : SPACER_Y_DESKTOP} />
          </AccordionItem>
        </Accordion>
      </Col>
      <LeaderboardListing selectedDate={stateSelectedDate} setSelectedDate={setStateSelectedDate} />
    </Col>
  );
}
